// Premium Mobile Theme - Binance/Bybit Style
// This file contains ONLY mobile styles - does not affect desktop

export const MOBILE_COLORS = {
  // Core
  background: '#0b0e11',
  backgroundSecondary: '#1e2329',
  backgroundTertiary: '#2b3139',
  card: '#181a20',
  cardHover: '#1e2329',
  
  // Brand
  primary: '#f0b90b',
  primaryHover: '#d4a300',
  primaryMuted: 'rgba(240, 185, 11, 0.15)',
  
  // Semantic
  success: '#0ecb81',
  successMuted: 'rgba(14, 203, 129, 0.15)',
  danger: '#f6465d',
  dangerMuted: 'rgba(246, 70, 93, 0.15)',
  warning: '#f0b90b',
  info: '#1e90ff',
  
  // Text
  textPrimary: '#eaecef',
  textSecondary: '#848e9c',
  textMuted: '#5e6673',
  textDisabled: '#474d57',
  
  // Borders
  border: '#2b3139',
  borderLight: '#363c45',
  borderFocus: '#f0b90b',
  
  // Gradients
  gradientPrimary: 'linear-gradient(135deg, #f0b90b 0%, #d4a300 100%)',
  gradientSuccess: 'linear-gradient(135deg, #0ecb81 0%, #0ba360 100%)',
  gradientDanger: 'linear-gradient(135deg, #f6465d 0%, #cf304a 100%)',
  gradientCard: 'linear-gradient(180deg, #1e2329 0%, #181a20 100%)',
};

export const MOBILE_SHADOWS = {
  sm: '0 2px 8px rgba(0, 0, 0, 0.3)',
  md: '0 4px 16px rgba(0, 0, 0, 0.4)',
  lg: '0 8px 32px rgba(0, 0, 0, 0.5)',
  glow: '0 0 20px rgba(240, 185, 11, 0.3)',
  glowSuccess: '0 0 20px rgba(14, 203, 129, 0.3)',
  glowDanger: '0 0 20px rgba(246, 70, 93, 0.3)',
};

export const MOBILE_SPACING = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
  xxxl: '32px',
};

export const MOBILE_TYPOGRAPHY = {
  // Font family
  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', 'Roboto', sans-serif",
  fontFamilyMono: "'SF Mono', 'Roboto Mono', 'Consolas', monospace",
  
  // Sizes
  h1: { fontSize: '28px', fontWeight: '700', lineHeight: '1.2' },
  h2: { fontSize: '22px', fontWeight: '700', lineHeight: '1.3' },
  h3: { fontSize: '18px', fontWeight: '600', lineHeight: '1.4' },
  h4: { fontSize: '16px', fontWeight: '600', lineHeight: '1.4' },
  body: { fontSize: '14px', fontWeight: '400', lineHeight: '1.5' },
  bodySmall: { fontSize: '13px', fontWeight: '400', lineHeight: '1.5' },
  caption: { fontSize: '12px', fontWeight: '400', lineHeight: '1.4' },
  tiny: { fontSize: '10px', fontWeight: '500', lineHeight: '1.3' },
  
  // Price displays
  priceMain: { fontSize: '24px', fontWeight: '700', fontFamily: "'SF Mono', monospace" },
  priceMedium: { fontSize: '18px', fontWeight: '600', fontFamily: "'SF Mono', monospace" },
  priceSmall: { fontSize: '14px', fontWeight: '600', fontFamily: "'SF Mono', monospace" },
};

export const MOBILE_BORDERS = {
  radius: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    full: '9999px',
  },
};

export const MOBILE_ANIMATIONS = {
  fast: '150ms ease-out',
  normal: '250ms ease-out',
  slow: '350ms ease-out',
  spring: '400ms cubic-bezier(0.175, 0.885, 0.32, 1.275)',
};

// Common mobile styles
export const mobileStyles = {
  safeAreaTop: 'max(12px, env(safe-area-inset-top))',
  safeAreaBottom: 'max(16px, env(safe-area-inset-bottom))',
  
  // Full screen container
  screenContainer: {
    minHeight: '100vh',
    backgroundColor: MOBILE_COLORS.background,
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'calc(72px + env(safe-area-inset-bottom))', // Bottom nav height
  },
  
  // Card styles
  card: {
    backgroundColor: MOBILE_COLORS.card,
    borderRadius: MOBILE_BORDERS.radius.lg,
    border: `1px solid ${MOBILE_COLORS.border}`,
    padding: MOBILE_SPACING.lg,
  },
  
  // Button styles
  buttonPrimary: {
    background: MOBILE_COLORS.gradientPrimary,
    color: '#0b0e11',
    border: 'none',
    borderRadius: MOBILE_BORDERS.radius.md,
    padding: '14px 20px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: MOBILE_ANIMATIONS.fast,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  
  buttonSecondary: {
    backgroundColor: MOBILE_COLORS.backgroundSecondary,
    color: MOBILE_COLORS.textPrimary,
    border: `1px solid ${MOBILE_COLORS.border}`,
    borderRadius: MOBILE_BORDERS.radius.md,
    padding: '14px 20px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: MOBILE_ANIMATIONS.fast,
  },
  
  buttonSuccess: {
    background: MOBILE_COLORS.gradientSuccess,
    color: '#ffffff',
    border: 'none',
    borderRadius: MOBILE_BORDERS.radius.md,
    padding: '14px 20px',
    fontSize: '15px',
    fontWeight: '700',
  },
  
  buttonDanger: {
    background: MOBILE_COLORS.gradientDanger,
    color: '#ffffff',
    border: 'none',
    borderRadius: MOBILE_BORDERS.radius.md,
    padding: '14px 20px',
    fontSize: '15px',
    fontWeight: '700',
  },
  
  // Input styles
  input: {
    backgroundColor: MOBILE_COLORS.backgroundSecondary,
    border: `1px solid ${MOBILE_COLORS.border}`,
    borderRadius: MOBILE_BORDERS.radius.md,
    padding: '14px 16px',
    fontSize: '15px',
    color: MOBILE_COLORS.textPrimary,
    outline: 'none',
    width: '100%',
    transition: MOBILE_ANIMATIONS.fast,
  },
};

export default {
  colors: MOBILE_COLORS,
  shadows: MOBILE_SHADOWS,
  spacing: MOBILE_SPACING,
  typography: MOBILE_TYPOGRAPHY,
  borders: MOBILE_BORDERS,
  animations: MOBILE_ANIMATIONS,
  styles: mobileStyles,
};
